<script setup lang="ts">
import { onBeforeUnmount, onMounted } from 'vue'
import { NButton, NEmpty, NSpin } from 'naive-ui'
import { useRepoCardHighlight } from '../composables/useRepoCardHighlight'
import RouteShell from './RouteShell.vue'
import AgentRepoDevcontainerStatus from './AgentRepoDevcontainerStatus.vue'
import AgentRepoCheckout from './AgentRepoCheckout.vue'
import AgentRepoPorts from './AgentRepoPorts.vue'
import AgentRepoProductOwner from './AgentRepoProductOwner.vue'
import AgentRepoActionsMenu from './AgentRepoActionsMenu.vue'
import AgentRepoAddForm from './AgentRepoAddForm.vue'
import AgentRepoTokenField from './AgentRepoTokenField.vue'
import { useAgentReposStore } from '../stores/agent_repos'
import { useDocumentTitle } from '../composables/useDocumentTitle'
import StatusPill from '../components/StatusPill.vue'
import { NEUTRAL_PILL_CLASS } from '../composables/statusPillColors'

// Admin page to manage the cloud-agent repo registry. Lists repos from
// both the committed YAML config and DB-managed rows (added here). Tokens are
// write-only: only "set / not set" status is shown, never the token itself.
// The live devcontainer status/metrics section (AgentRepoDevcontainerStatus),
// the token fields (AgentRepoTokenField) and the add-repo form
// (AgentRepoAddForm) are extracted to keep this file under the 300-line limit.
const store = useAgentReposStore()
useDocumentTitle('Agent repos')

// Rings the repo card named by a `?repo=` deep-link from AgentView's
// session context line (#1362).
const { highlightedSlug, highlightFromQuery } = useRepoCardHighlight((slug) => store.repos.some((r) => r.slug === slug))

// Devcontainer status/metrics are polled rather than pushed live (#842) —
// this is an occasionally-checked ops page, not something watched
// continuously, so a simple interval is enough (mirrors ThreadDrawer.vue's
// only other setInterval precedent in this codebase).
//
// 30s rather than the original 15s: the metrics behind this response are now
// measured by a job on the agent box roughly once a minute, so polling faster
// than that only re-fetches numbers that haven't moved.
const STATUS_POLL_MS = 30_000
let pollTimer: ReturnType<typeof setInterval> | null = null

// Also wired to the Retry button next to the load error — without it the
// only recovery from a failed initial load is a full browser refresh.
async function loadRepos(): Promise<void> {
  try {
    await store.fetchAll()
  } catch {
    /* loadError surfaces in template */
  }
}

onMounted(async () => {
  await loadRepos()
  pollTimer = setInterval(() => {
    void store.fetchAll().catch(() => {})
  }, STATUS_POLL_MS)
  await highlightFromQuery()
})

onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer)
})
</script>

<template>
  <RouteShell>
    <section class="max-w-3xl mx-auto flex flex-col gap-4 pb-6">
      <header class="flex flex-col gap-1">
        <h1 class="text-2xl font-semibold text-white m-0">Agent repos</h1>
        <p class="text-sm text-white/60 m-0">
          Manage which repos the cloud agent can work on. Add a repo here to store its GitHub PAT encrypted — no
          redeploy or SSH needed. YAML-backed repos (from <code>config/agent_repos.yml</code>) appear as read-only; save
          a token here to override their ENV-sourced credentials.
        </p>
        <p class="text-sm text-white/60 m-0" data-testid="agent-repos-latest-cli-version">
          Latest Claude Code CLI available:
          <span class="text-white/80 font-mono">{{ store.latestCliVersion ?? 'Unknown' }}</span>
        </p>
      </header>

      <div v-if="store.loading && !store.loaded" class="flex items-center justify-center py-12">
        <NSpin />
      </div>

      <div
        v-else-if="store.loadError"
        class="flex items-center gap-3 text-red-300 text-sm"
        data-testid="agent-repos-load-error"
      >
        <span>Failed to load repos: {{ store.loadError }}</span>
        <NButton size="tiny" secondary :loading="store.loading" data-testid="agent-repos-load-retry" @click="loadRepos">
          Retry
        </NButton>
      </div>

      <NEmpty v-else-if="store.repos.length === 0" description="No repos configured." class="my-12" />

      <ul v-else class="list-none p-0 m-0 flex flex-col gap-4" data-testid="agent-repo-list">
        <li
          v-for="repo in store.repos"
          :key="repo.slug"
          class="rounded border border-white/10 bg-black/20 p-4 flex flex-col gap-3 transition-shadow"
          :class="{ 'ring-2 ring-brand-primary/60': repo.slug === highlightedSlug }"
          :data-testid="`agent-repo-${repo.slug}`"
        >
          <!-- Header row: slug + source badge + advanced-actions menu -->
          <div class="flex items-center gap-3">
            <h2 class="text-base font-semibold text-white m-0 font-mono">{{ repo.slug }}</h2>
            <StatusPill
              :label="repo.source === 'db' ? 'DB' : 'YAML'"
              :color-class="repo.source === 'db' ? 'bg-brand-primary/15 text-brand-primary/90' : NEUTRAL_PILL_CLASS"
              :data-testid="`agent-repo-source-${repo.slug}`"
            />
            <StatusPill
              v-if="repo.is_default"
              label="Default"
              color-class="bg-emerald-500/15 text-emerald-300/90"
              :data-testid="`agent-repo-default-${repo.slug}`"
            />
            <AgentRepoActionsMenu :repo="repo" class="ml-auto" />
          </div>

          <!-- Status grid -->
          <dl class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs m-0">
            <template v-if="repo.github_repo">
              <dt class="text-white/40">Repository</dt>
              <dd class="m-0 text-white/70 font-mono">{{ repo.github_repo }}</dd>
            </template>
            <AgentRepoPorts :repo="repo" :all-repos="store.repos" />
            <dt class="text-white/40">GitHub PAT</dt>
            <dd
              class="m-0"
              :class="repo.token_set ? 'text-emerald-300/90' : 'text-white/40'"
              :data-testid="`agent-repo-token-status-${repo.slug}`"
            >
              {{ repo.token_set ? 'Set' : 'Not set' }}
            </dd>
            <dt class="text-white/40">Registry token</dt>
            <dd
              class="m-0"
              :class="repo.registry_token_set ? 'text-emerald-300/90' : 'text-white/40'"
              :data-testid="`agent-repo-registry-token-status-${repo.slug}`"
            >
              {{ repo.registry_token_set ? 'Set' : 'Not set' }}
            </dd>
            <dt class="text-white/40">Checkout</dt>
            <dd
              class="m-0"
              :class="repo.checkout_exists ? 'text-emerald-300/90' : 'text-amber-300/80'"
              :data-testid="`agent-repo-checkout-${repo.slug}`"
            >
              {{ repo.checkout_exists ? 'Present' : 'Missing' }}
            </dd>
            <dt class="text-white/40">Devcontainer</dt>
            <dd
              class="m-0"
              :class="repo.devcontainer_exists ? 'text-emerald-300/90' : 'text-amber-300/80'"
              :data-testid="`agent-repo-devcontainer-${repo.slug}`"
            >
              {{ repo.devcontainer_exists ? 'Present' : 'Missing' }}
            </dd>
            <AgentRepoDevcontainerStatus :repo="repo" :latest-cli-version="store.latestCliVersion" />
            <AgentRepoCheckout :repo="repo" />
          </dl>

          <!-- Token update + actions -->
          <div class="flex flex-col gap-2 border-t border-white/10 pt-3">
            <AgentRepoTokenField
              :slug="repo.slug"
              param="github_token"
              label="GitHub PAT"
              :is-set="repo.token_set"
              placeholder="ghp_…"
              testid-prefix="agent-repo-token"
            />
            <AgentRepoTokenField
              :slug="repo.slug"
              param="registry_token"
              label="registry token"
              :is-set="repo.registry_token_set"
              placeholder="ghp_… (classic PAT with read:packages)"
              testid-prefix="agent-repo-registry-token"
              hint="Only needed when this repo's compose stack pulls a private ghcr.io image."
            />
            <AgentRepoProductOwner :repo="repo" />
          </div>
        </li>
      </ul>

      <AgentRepoAddForm />
    </section>
  </RouteShell>
</template>
